import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { listDepartmentsQuery } from '@/queries/department.query'
import {
  listAgentTicketsQuery,
  listDeskTicketsQuery,
  listFinishedTicketsQuery,
  listPendingTicketsQuery,
} from '@/queries/ticket.query'
import { listWhatsappConnectionQuery } from '@/queries/whatsapp.query'
import { TicketService } from '@/services/ticket.service'
import {
  selectFocusTicket,
  selectSetActiveTab,
  useChatStore,
} from '@/stores/chat.store'
import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { IconTrash } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { Row } from '@tanstack/react-table'
import { useState } from 'react'
import { toast } from 'sonner'
import { useContacts } from '../context/contacts-context'
import { contactSchema } from '../schema'

interface DataTableRowActionsProps<TData> {
  readonly row: Row<TData>
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const contact = contactSchema.parse(row.original)
  const { setOpen, setCurrentRow } = useContacts()
  const router = useRouter()
  const queryClient = useQueryClient()
  const focusTicket = useChatStore(selectFocusTicket)
  const setActiveTab = useChatStore(selectSetActiveTab)
  const [isStartingChat, setIsStartingChat] = useState(false)

  async function handleChat() {
    if (isStartingChat) {
      return
    }

    setIsStartingChat(true)

    try {
      const [departments, whatsappConnections] = await Promise.all([
        queryClient.fetchQuery(listDepartmentsQuery),
        queryClient.fetchQuery(listWhatsappConnectionQuery),
      ])

      const selectedDepartment = departments[0]
      const selectedWhatsapp = whatsappConnections[0]

      if (!selectedDepartment || !selectedWhatsapp) {
        toast.error(
          'Não foi possível iniciar o chat. Configure departamento e conexão WhatsApp.'
        )
        return
      }

      const result = await TicketService.create({
        contactIds: [contact.id],
        departmentId: Number(selectedDepartment.id),
        whatsappId: selectedWhatsapp.id,
      })

      const firstTicket = result.created[0] || result.reused[0]

      if (!firstTicket) {
        toast.error('Não foi possível iniciar o chat para este contato.')
        return
      }

      const joinedTicket = await TicketService.joinChat(firstTicket.id)

      await Promise.all([
        queryClient.invalidateQueries(listAgentTicketsQuery),
        queryClient.invalidateQueries(listDeskTicketsQuery),
        queryClient.invalidateQueries(listPendingTicketsQuery),
        queryClient.invalidateQueries(listFinishedTicketsQuery),
      ])

      setActiveTab('atendendo')
      focusTicket(joinedTicket.id)
      router.navigate({ to: '/chats' })
      toast.success('Chat aberto com sucesso')
    } catch {
      toast.error('Erro ao abrir chat')
    } finally {
      setIsStartingChat(false)
    }
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
        >
          <DotsHorizontalIcon className='h-4 w-4' />
          <span className='sr-only'>Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-[160px]'>
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(contact)
            setOpen('update')
          }}
        >
          Editar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleChat} disabled={isStartingChat}>
          {isStartingChat ? 'Abrindo...' : 'Conversar'}
        </DropdownMenuItem>
        <DropdownMenuItem disabled>Enviar fluxo</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(contact)
            setOpen('delete')
          }}
        >
          Excluir
          <DropdownMenuShortcut>
            <IconTrash size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
